import {
  PaidPricingTableKey,
  PlanName,
  PlanNameEnum,
} from 'base/accounts_service';
import { AnalyticsService } from 'base/analytics';
import { navigateInternal } from 'base/navigation';
import { Button } from 'base/ui/button';
import { DialogLayer } from 'base/ui/dialog';
import {
  IconCheck,
  IconClear,
  IconCode,
  IconFigma,
  IconReact,
  IconWebflow,
} from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { TextRegularLight, TextSmall, Title5 } from 'base/ui/typography';
import { useOnMount } from 'base/useOnMount';
import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import exportHeroImage from './feature-gate-export.webp';
import pagesHeroImage from './feature-gate-pages.webp';
import projectsHeroImage from './feature-gate-projects.webp';
import shareHeroImage from './feature-gate-share.webp';
import teamHeroImage from './feature-gate-team.webp';
import styles from './feature_gate_dialog.css';

export function ProFeatureGateDialog({
  analytics,
  currentPlan,
  initialFeatureKey,
  onClose,
}: {
  analytics: AnalyticsService;
  currentPlan: PlanNameEnum;
  initialFeatureKey: 'projects' | 'pages' | 'share' | 'export' | 'unlock';
  onClose: () => void;
}) {
  const [featureKey, setFeatureKey] = useState(initialFeatureKey);
  const { title, description, image } = {
    unlock: {
      title: 'Unlock this Project',
      description: `This project was moved to a view-only state with comments disabled, as your plan includes only 1 project. Upgrade to lift the restriction.`,
      image: projectsHeroImage,
    },
    projects: {
      title: 'Create unlimited projects',
      description: `Lift the 1-project ${
        currentPlan === PlanName.FREE ? 'Free' : 'Starter'
      } plan limit and upgrade to Pro to create unlimited projects for you and your clients`,
      image: projectsHeroImage,
    },
    pages: {
      title: 'Create all pages',
      description: `Lift the ${
        currentPlan === PlanName.FREE ? '1-page Free' : '5-page Starter'
      } plan limit and upgrade to unlock all wireframe pages, enabling you to start conceptualizing larger websites`,
      image: pagesHeroImage,
    },
    share: {
      title: 'Unlock commenting',
      description:
        'Upgrade to share your projects with commenting functionality, enabling contextual feedback to finalize projects faster',
      image: shareHeroImage,
    },
    export: {
      title: 'Export your project',
      description:
        'Upgrade to export all pages and Style Guides into the platform of your choice to continue building and publishing your website',
      image: exportHeroImage,
    },
  }[featureKey];

  useOnMount(() =>
    analytics.track({
      event: 'upgrade_flow:paywall_modal_view',
      feature: featureKey,
    }),
  );

  const handleClose = useCallback(() => {
    analytics.track({
      event: 'upgrade_flow:paywall_close_button_click',
      feature: featureKey,
    });
    onClose();
  }, [analytics, featureKey, onClose]);

  return (
    <DialogLayer onClose={handleClose}>
      <FeatureGate
        onClickFeature={setFeatureKey}
        planName="Pro"
        title={title}
        description={description}
        features={[
          {
            key: initialFeatureKey === 'unlock' ? 'unlock' : 'projects',
            label: 'Unlimited projects',
          },
          { key: 'pages', label: 'Unlimited pages' },
          { key: 'share', label: 'Share – Commenting' },
          {
            key: 'export',
            label: 'Export your project',
            icons: <ExportIconList />,
          },
        ]}
        featureKey={featureKey}
        image={image}
        targetPlanKey="proYearly"
        onClose={handleClose}
        analytics={analytics}
      />
    </DialogLayer>
  );
}

export function TeamFeatureGateDialog({
  analytics,
  onClose,
}: {
  analytics: AnalyticsService;
  onClose: () => void;
}) {
  useOnMount(() =>
    analytics.track({
      event: 'upgrade_flow:paywall_modal_view',
      feature: 'team',
    }),
  );

  const handleClose = useCallback(() => {
    analytics.track({
      event: 'upgrade_flow:paywall_close_button_click',
      feature: 'team',
    });
    onClose();
  }, [analytics, onClose]);

  return (
    <DialogLayer onClose={handleClose}>
      <TeamFeatureGate onClose={handleClose} analytics={analytics} />
    </DialogLayer>
  );
}

export function TeamFeatureGate({
  onClose,
  fullWidth,
  bordered,
  analytics,
}: {
  onClose?: () => void;
  fullWidth?: boolean;
  bordered?: boolean;
  analytics: AnalyticsService;
}) {
  return (
    <FeatureGate
      planName="Team"
      title="A branded experience for \n your team to collaborate"
      description="Join Relume Team and get 3 seats included for your team. Add and remove seats as needed"
      features={[
        { key: '1', label: '3 Users included' },
        { key: '2', label: 'Each user with Pro features' },
        { key: '3', label: 'Team workspace' },
        { key: '4', label: 'Branded sharing' },
      ]}
      image={teamHeroImage}
      onClose={onClose}
      targetPlanKey="teamYearly"
      hideAllPlans
      fullWidth={fullWidth}
      bordered={bordered}
      analytics={analytics}
    />
  );
}

function ExportIconList() {
  return (
    <Inline spacing={8}>
      {[IconFigma, IconWebflow, IconReact, IconCode].map((Icon, index) => (
        <Icon width={16} key={index} />
      ))}
    </Inline>
  );
}

export function FeatureGate<FeatureKey extends string>({
  featureKey,
  planName,
  title,
  description,
  features,
  image,
  onClickFeature,
  onClose,
  hideAllPlans,
  targetPlanKey,
  fullWidth = false,
  bordered = false,
  analytics,
}: {
  featureKey?: FeatureKey;
  planName: string;
  title: string;
  description: string;
  features: { key: FeatureKey; label: string; icons?: React.ReactNode }[];
  image: string;
  onClickFeature?: (key: FeatureKey) => void;
  onClose?: () => void;
  hideAllPlans?: true;
  targetPlanKey: PaidPricingTableKey;
  fullWidth?: boolean;
  bordered?: boolean;
  analytics: AnalyticsService;
}) {
  return (
    <div
      className={classNames(
        styles.container,
        fullWidth && styles.fullWidth,
        bordered && styles.bordered,
      )}
    >
      <img
        alt="Hero image for feature gate"
        src={image}
        className={styles.heroImage}
      />
      <div className={styles.upgradeSell}>
        <Rows spacing={16} yalign="space-between" fixedHeight>
          <Rows>
            <Inline xalign="space-between">
              <Rows spacing={0} xalign="start">
                <TextSmall color="gradient">Upgrade required</TextSmall>
                {title.split(/\n|\\n/).map((s, i) => (
                  <Title5 key={i}>{s.trim()}</Title5>
                ))}
              </Rows>
              {onClose && (
                <Button
                  size="large"
                  variant="tertiary"
                  onClick={onClose}
                  icon={IconClear}
                />
              )}
            </Inline>

            <TextRegularLight>{description}</TextRegularLight>

            <div style={{ padding: '8px 0' }}>
              <Rows spacing={0}>
                {features.map(({ key, label, icons }) => (
                  <Button
                    onClick={() => onClickFeature?.(key)}
                    key={key}
                    size="medium"
                    variant="tertiary"
                    icon={IconCheck}
                    label={label}
                    xalign="left"
                    background={key === featureKey ? 'grey3' : undefined}
                  >
                    {icons}
                  </Button>
                ))}
              </Rows>
            </div>
          </Rows>

          <Inline xalign="end">
            {hideAllPlans ? null : (
              <Button
                label="Full comparison"
                size="large"
                href={authRouteToUrl({
                  path: 'upgrade',
                  context: { redirect: getCurrentLocationForRedirect() },
                })}
                onClick={(e) => {
                  navigateInternal(e);
                  analytics.track({
                    event: 'upgrade_flow:paywall_compare_plans_button_click',
                    feature: featureKey ?? 'team',
                  });
                }}
              />
            )}
            <Button
              label={`Upgrade to ${planName}`}
              variant="primary"
              size="large"
              href={authRouteToUrl({
                path: 'upgrade',
                context: {
                  redirect: getCurrentLocationForRedirect(),
                  selectPlan: targetPlanKey,
                },
              })}
              onClick={(e) => {
                navigateInternal(e);
                analytics.track({
                  event: 'upgrade_flow:paywall_upgrade_button_click',
                  feature: featureKey ?? 'team',
                });
              }}
              gradient
            />
          </Inline>
        </Rows>
      </div>
    </div>
  );
}
